/**
 * Coss Discount Rules — Display Engine
 * Reads rules from #cs-discount-rules data container,
 * evaluates them against live cart, renders banners in #cs-discount-display.
 */
(function () {
  'use strict';

  /* ─── IST date handling ─────────────────────────────────── */
  // IST = UTC + 5h30m = UTC + 330 min

  function parseIST(str) {
    if (!str || !str.trim()) return null;
    // Expected format: "YYYY-MM-DD HH:MM"
    var parts = str.trim().split(' ');
    if (parts.length < 2) return null;
    var d = parts[0].split('-');
    var t = parts[1].split(':');
    if (d.length < 3 || t.length < 2) return null;
    // Build the UTC moment that corresponds to this IST instant (subtract 5h30m)
    return new Date(Date.UTC(
      parseInt(d[0], 10),
      parseInt(d[1], 10) - 1,
      parseInt(d[2], 10),
      parseInt(t[0], 10),
      parseInt(t[1], 10)
    ) - 330 * 60 * 1000);
  }

  function isValidNow(starts, ends) {
    var now = Date.now();
    if (starts) { var s = parseIST(starts); if (s && now < s.getTime()) return false; }
    if (ends)   { var e = parseIST(ends);   if (e && now > e.getTime()) return false; }
    return true;
  }

  /* ─── Read rules from DOM ───────────────────────────────── */
  function readRules() {
    var rules = [];
    document.querySelectorAll('#cs-discount-rules .cs-dr').forEach(function (el) {
      var colIds = (el.dataset.colIds || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      rules.push({
        label:          el.dataset.label        || '',
        code:           el.dataset.code         || '',
        description:    el.dataset.desc         || '',
        type:           el.dataset.dtype        || 'percentage',
        value:          parseFloat(el.dataset.dvalue)    || 0,
        cap:            parseFloat(el.dataset.cap)       || 0,
        colIds:         colIds,
        colTitle:       el.dataset.colTitle     || '',
        prodId:         el.dataset.prodId       || '',
        prodTitle:      el.dataset.prodTitle    || '',
        minCart:        parseFloat(el.dataset.minCart)   || 0,
        maxCart:        parseFloat(el.dataset.maxCart)   || 0,
        minQty:         parseInt(el.dataset.minQty, 10)  || 0,
        maxQty:         parseInt(el.dataset.maxQty, 10)  || 0,
        getQty:         parseInt(el.dataset.getQty, 10)  || 1,
        perCustomer:    parseInt(el.dataset.perCustomer, 10) || 0,
        startsAt:       el.dataset.starts       || '',
        endsAt:         el.dataset.ends         || '',
        showInCart:     el.dataset.showCart !== 'false',
        stackable:      el.dataset.stackable    === 'true',
        color:          el.dataset.color        || '#d2cc39',
        eligibleMsg:    el.dataset.eligibleMsg  || '',
        progressMsg:    el.dataset.progressMsg  || '',
        hideWhenEmpty:  el.dataset.hideEmpty    === 'true'
      });
    });
    return rules;
  }

  /* ─── Evaluate a single rule against cart ──────────────── */
  function evaluate(rule, cart) {
    var items       = cart.items || [];
    var cartValueRs = (cart.original_total_price || cart.total_price || 0) / 100;

    // Qualifying items (filtered by product/collection if set)
    var qualItems;
    if (rule.prodId) {
      qualItems = items.filter(function (i) { return String(i.product_id) === rule.prodId; });
    } else if (rule.colIds.length > 0) {
      qualItems = items.filter(function (i) { return rule.colIds.indexOf(String(i.product_id)) !== -1; });
    } else {
      qualItems = items;
    }

    var qualQty = qualItems.reduce(function (s, i) { return s + i.quantity; }, 0);

    // BOGO: eligible only when buyQty + getQty items are all in the cart
    if (rule.type === 'bogo') {
      var buyQty    = Math.max(1, rule.minQty);   // at least 1 must be bought
      var bogoNeeded = buyQty + rule.getQty;      // e.g. buy 1 + get 1 = 2 total
      if (qualQty === 0 && (rule.prodId || rule.colIds.length > 0)) {
        if (rule.hideWhenEmpty) return { eligible: false, progress: null };
        var bogoFilter = rule.prodTitle || rule.colTitle || 'eligible items';
        return { eligible: false, progress: { type: 'no_items', current: 0, needed: bogoNeeded, remaining: bogoNeeded, pct: 0, filterName: bogoFilter } };
      }
      if (qualQty < bogoNeeded) {
        return {
          eligible: false,
          progress: { type: 'quantity', current: qualQty, needed: bogoNeeded, remaining: bogoNeeded - qualQty, pct: Math.min(99, Math.round((qualQty / bogoNeeded) * 100)) }
        };
      }
      return { eligible: true, progress: null };
    }

    // Hide entirely when no qualifying items and rule is set to show only with qualifying items
    if (rule.hideWhenEmpty && (rule.prodId || rule.colIds.length > 0) && qualQty === 0) {
      return { eligible: false, progress: null };
    }

    // Hard stops — over max limits
    if (rule.maxCart > 0 && cartValueRs > rule.maxCart) return { eligible: false, progress: null };
    if (rule.maxQty  > 0 && qualQty > rule.maxQty)      return { eligible: false, progress: null };

    // Min cart value: scoped to qualifying-items subtotal when rule targets a product/collection
    if (rule.minCart > 0) {
      var minCartValue = (rule.prodId || rule.colIds.length > 0)
        ? qualItems.reduce(function (s, i) { return s + (i.original_line_price || 0); }, 0) / 100
        : cartValueRs;
      if (minCartValue < rule.minCart) {
        return {
          eligible: false,
          progress: { type: 'cart_value', current: minCartValue, needed: rule.minCart, remaining: rule.minCart - minCartValue, pct: Math.min(99, Math.round((minCartValue / rule.minCart) * 100)) }
        };
      }
    }

    // Min quantity of qualifying items
    if (rule.minQty > 0 && qualQty < rule.minQty) {
      return {
        eligible: false,
        progress: { type: 'quantity', current: qualQty, needed: rule.minQty, remaining: rule.minQty - qualQty, pct: Math.min(99, Math.round((qualQty / rule.minQty) * 100)) }
      };
    }

    // Has a collection/product filter but nothing qualifying in cart yet
    if ((rule.prodId || rule.colIds.length > 0) && qualQty === 0 && rule.minCart <= 0) {
      if (rule.hideWhenEmpty) return { eligible: false, progress: null };
      var filterName = rule.prodTitle || rule.colTitle || 'eligible items';
      return {
        eligible: false,
        progress: { type: 'no_items', current: 0, needed: 1, remaining: 1, pct: 0, filterName: filterName }
      };
    }

    return { eligible: true, progress: null };
  }

  /* ─── Savings estimate ──────────────────────────────────── */
  function calcSavings(rule, cart) {
    var items = cart.items || [];

    // BOGO: savings = what the automatic discount already applied (original - final)
    if (rule.type === 'bogo') {
      var bogoItems = rule.prodId
        ? items.filter(function (i) { return String(i.product_id) === rule.prodId; })
        : rule.colIds.length > 0
          ? items.filter(function (i) { return rule.colIds.indexOf(String(i.product_id)) !== -1; })
          : items;
      return Math.round(bogoItems.reduce(function (s, i) {
        return s + ((i.original_line_price || 0) - (i.final_line_price || 0));
      }, 0) / 100);
    }

    if (!rule.value) return 0;

    // Use original_line_price as base so savings reflect the pre-discount price.
    // BOGO free items are handled separately above; remaining rules estimate from original price.
    // Shopify combines these rules in PARALLEL, not sequentially: each eligible discount
    // is computed independently off the same pre-promo subtotal and the amounts are summed
    // (verified against a real checkout receipt — 20% and 5% both off the same base, not
    // one compounding on the other). So every rule, including cart-wide ones, uses the
    // same original (pre-discount) base.
    function lp(i) { return i.original_line_price != null ? i.original_line_price : (i.final_line_price || 0); }

    var baseRs;
    if (rule.prodId) {
      var q = items.filter(function (i) { return String(i.product_id) === rule.prodId; });
      baseRs = q.reduce(function (s, i) { return s + lp(i); }, 0) / 100;
    } else if (rule.colIds.length > 0) {
      var q2 = items.filter(function (i) { return rule.colIds.indexOf(String(i.product_id)) !== -1; });
      baseRs = q2.reduce(function (s, i) { return s + lp(i); }, 0) / 100;
    } else {
      // Cart-wide rule: when a collection-scoped BOGO is active, exclude its items
      // so the cart-wide discount reflects only what's not already covered by BOGO.
      var baseItems = items;
      if (_activeBogos.some(function (b) { return !b.stackable; })) {
        var bogoIds = [];
        var bogoIsCartWide = false;
        _activeBogos.forEach(function (b) {
          if (b.prodId)              bogoIds.push(b.prodId);
          else if (b.colIds.length)  bogoIds = bogoIds.concat(b.colIds);
          else                       bogoIsCartWide = true;
        });
        if (bogoIsCartWide) {
          baseItems = [];
        } else if (bogoIds.length) {
          baseItems = items.filter(function (i) {
            return bogoIds.indexOf(String(i.product_id)) === -1;
          });
        }
      }
      baseRs = baseItems.reduce(function (s, i) { return s + lp(i); }, 0) / 100;
    }
    var savings = rule.type === 'percentage' ? baseRs * (rule.value / 100) : rule.value;
    if (rule.cap > 0 && savings > rule.cap) savings = rule.cap;
    return Math.round(savings);
  }

  /* ─── Helpers ───────────────────────────────────────────── */
  function fmtRs(n) { return '₹' + Math.round(n).toLocaleString('en-IN'); }
  function esc(s)   { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

  function copyCode(code, btn) {
    var done = function () {
      var orig = btn.textContent;
      btn.textContent = '✓ Copied!';
      btn.style.background = '#15803d';
      setTimeout(function () { btn.textContent = orig; btn.style.background = ''; }, 2200);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(done).catch(function () {
        fallbackCopy(code, done);
      });
    } else {
      fallbackCopy(code, done);
    }
  }
  function fallbackCopy(code, cb) {
    var ta = document.createElement('textarea');
    ta.value = code; ta.style.cssText = 'position:fixed;opacity:0;';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    ta.remove(); cb();
  }

  /* ─── Build eligible card ───────────────────────────────── */
  function buildEligible(rule, cart) {
    var savings = calcSavings(rule, cart);
    var discountStr;
    if (rule.type === 'bogo') {
      // Calculate actual free-item count from cart quantity
      var bogoQualItems = rule.prodId
        ? (cart.items || []).filter(function (i) { return String(i.product_id) === rule.prodId; })
        : rule.colIds.length > 0
          ? (cart.items || []).filter(function (i) { return rule.colIds.indexOf(String(i.product_id)) !== -1; })
          : (cart.items || []);
      var bogoQualQty = bogoQualItems.reduce(function (s, i) { return s + i.quantity; }, 0);
      var bBuyQty  = Math.max(1, rule.minQty);
      var bNeeded  = bBuyQty + rule.getQty;
      var freeCount = Math.floor(bogoQualQty / bNeeded) * rule.getQty;
      if (freeCount < rule.getQty) freeCount = rule.getQty; // at minimum show 1 cycle

      // Cross-check with Shopify's natively-applied discount for accuracy
      if (savings > 0 && bogoQualQty > 0) {
        var totalPaise = bogoQualItems.reduce(function (s, i) { return s + i.price * i.quantity; }, 0);
        var avgItemPriceRs = (totalPaise / bogoQualQty) / 100; // convert paise → Rs
        if (avgItemPriceRs > 0) {
          var nativeFree = Math.round(savings / avgItemPriceRs);
          if (nativeFree > 0 && nativeFree <= bogoQualQty) freeCount = nativeFree;
        }
      }

      discountStr = freeCount + ' FREE item' + (freeCount !== 1 ? 's' : '') + ' applied';
    } else if (rule.type === 'percentage') {
      discountStr = rule.value + '% off';
    } else {
      discountStr = fmtRs(rule.value) + ' off';
    }
    if (rule.type !== 'bogo' && rule.cap > 0) discountStr += ' (max ' + fmtRs(rule.cap) + ')';

    var msg = rule.eligibleMsg || (esc(rule.label) + ' unlocked! 🎉');

    var card = document.createElement('div');
    card.className = 'cs-disc-card cs-disc-card--eligible';
    card.style.setProperty('--cs-disc-color', rule.color);

    /* Left column */
    var leftHtml =
      '<div class="cs-disc-card__top">' +
        '<span class="cs-disc-card__icon">✅</span>' +
        '<span class="cs-disc-card__msg">' + msg + '</span>' +
      '</div>';
    if (rule.type === 'bogo') {
      // Always show free item count — savings only appear once the code is applied at checkout
      leftHtml += '<div class="cs-disc-card__savings">' + discountStr +
        (savings > 0 ? ' · Save ' + fmtRs(savings) : '') +
      '</div>';
    } else if (savings > 0) {
      leftHtml += '<div class="cs-disc-card__savings">Save ' + fmtRs(savings) + ' (' + discountStr + ')</div>';
    }
    if (rule.description) {
      leftHtml += '<p class="cs-disc-card__desc">' + esc(rule.description) + '</p>';
    }

    /* Right column — code + copy button stacked */
    var rightHtml = '';
    if (rule.code) {
      rightHtml =
        '<div class="cs-disc-card__right">' +
          '<span class="cs-disc-card__code">' + esc(rule.code) + '</span>' +
          '<button class="cs-disc-card__copy" type="button">Copy code</button>' +
        '</div>';
    }

    card.innerHTML =
      '<div class="cs-disc-card__inner">' +
        '<div class="cs-disc-card__left">' + leftHtml + '</div>' +
        rightHtml +
      '</div>';

    if (rule.code) {
      card.querySelector('.cs-disc-card__copy').addEventListener('click', function () {
        copyCode(rule.code, this);
      });
    }

    return card;
  }

  /* ─── Build progress card ───────────────────────────────── */
  function buildProgress(rule, progress) {
    var msg = '';
    var subLabel = '';

    if (progress.type === 'no_items') {
      var target = progress.filterName;
      if (rule.minQty > 0) {
        msg = rule.progressMsg
          ? rule.progressMsg.replace('{remaining}', progress.remaining + ' item(s) from ' + target)
          : 'Add ' + rule.minQty + ' item(s) from <strong>' + esc(target) + '</strong> to get ' + esc(rule.label) + '!';
      } else {
        msg = rule.progressMsg
          ? rule.progressMsg.replace('{remaining}', 'items from ' + target)
          : 'Add items from <strong>' + esc(target) + '</strong> to unlock ' + esc(rule.label) + '!';
      }
    } else if (progress.type === 'cart_value') {
      var rem = fmtRs(progress.remaining);
      msg = rule.progressMsg
        ? rule.progressMsg.replace('{remaining}', rem)
        : 'Add ' + rem + ' more to unlock <strong>' + esc(rule.label) + '</strong>!';
      subLabel = fmtRs(progress.current) + ' / ' + fmtRs(progress.needed);
    } else {
      var remQty = progress.remaining + ' more item' + (progress.remaining > 1 ? 's' : '');
      msg = rule.progressMsg
        ? rule.progressMsg.replace('{remaining}', remQty)
        : 'Add ' + remQty + ' to unlock <strong>' + esc(rule.label) + '</strong>!';
      subLabel = progress.current + ' / ' + progress.needed + ' item' + (progress.needed > 1 ? 's' : '');
    }

    if (rule.description) msg += '<p class="cs-disc-card__desc" style="margin-top:4px;">' + esc(rule.description) + '</p>';

    var isEmpty = progress.pct === 0;
    var card = document.createElement('div');
    card.className = 'cs-disc-card cs-disc-card--progress' + (isEmpty ? ' cs-disc-card--empty-progress' : '');
    card.style.setProperty('--cs-disc-color', rule.color);

    var leftHtml =
      '<div class="cs-disc-card__top">' +
        '<span class="cs-disc-card__icon">🎁</span>' +
        '<span class="cs-disc-card__msg">' + msg + '</span>' +
      '</div>';

    if (progress.type !== 'no_items' || rule.minQty > 0) {
      leftHtml +=
        '<div class="cs-disc-progress">' +
          '<div class="cs-disc-progress__bar" style="width:' + progress.pct + '%"></div>' +
        '</div>';
      if (subLabel) leftHtml += '<p class="cs-disc-progress__label">' + esc(subLabel) + '</p>';
    }

    card.innerHTML =
      '<div class="cs-disc-card__inner">' +
        '<div class="cs-disc-card__left">' + leftHtml + '</div>' +
      '</div>';

    return card;
  }

  /* ─── Savings preview (all eligible rules, below subtotal) ─ */
  function updateSavingsPreview(cart) {
    var container = document.getElementById('cs-savings-preview');
    if (!container) return;

    container.innerHTML = '';

    var rules = readRules();
    if (!rules.length) return;

    var activeBogos = rules.filter(function (rule) {
      return rule.type === 'bogo' && isValidNow(rule.startsAt, rule.endsAt) && evaluate(rule, cart).eligible;
    });

    // Collect ALL eligible non-BOGO rules and their savings
    var allEligible = [];
    rules.forEach(function (rule) {
      if (!rule.showInCart) return;
      if (rule.type === 'bogo') return;
      if (!isValidNow(rule.startsAt, rule.endsAt)) return;
      if (activeBogos.some(function (b) { return bogoCoversAllOf(b, rule, cart.items || []); })) return;
      var result = evaluate(rule, cart);
      if (result.eligible) {
        var s = calcSavings(rule, cart);
        if (s > 0) allEligible.push({ rule: rule, savings: s });
      }
    });

    if (!allEligible.length) return;

    var totalSavings = allEligible.reduce(function (s, e) { return s + e.savings; }, 0);
    if (totalSavings <= 0) return;

    // Pay ≈ subtracts only manual-code savings (auto discounts already in cart.total_price)
    var totalRs = (cart.total_price || 0) / 100;
    var manualSavings = allEligible.reduce(function (s, e) { return s + (e.rule.code ? e.savings : 0); }, 0);
    var payRs = Math.max(0, totalRs - manualSavings);

    var el = document.createElement('div');
    el.className = 'cs-savings-preview';

    var inner = '<span class="cs-savings-preview__icon">🏷</span>';
    var codeRules = allEligible.filter(function (e) { return e.rule.code; });

    // totalRs already nets out auto-applied rules (no code), so when a code is what's
    // being offered here, "Save" must be the code's own marginal saving only — using
    // totalSavings (auto + manual) would double-count the auto part against payRs,
    // which already only subtracts manualSavings.
    var autoSaveStr = 'Save <strong>' + fmtRs(totalSavings) + '</strong>';
    var codeSaveStr = 'Save <strong>' + fmtRs(manualSavings) + '</strong>';
    var payStr  = ' · Pay ≈ <strong>' + fmtRs(payRs) + '</strong>';

    if (codeRules.length === 1) {
      inner += '<span class="cs-savings-preview__text">Use <strong>' + esc(codeRules[0].rule.code) + '</strong>' +
        ' → ' + codeSaveStr + payStr + '</span>';
    } else if (codeRules.length > 1) {
      var codeList = codeRules.map(function (e) { return '<strong>' + esc(e.rule.code) + '</strong>'; }).join(' + ');
      inner += '<span class="cs-savings-preview__text">Use ' + codeList + ' → ' + codeSaveStr + payStr + '</span>';
    } else {
      inner += '<span class="cs-savings-preview__text">' + autoSaveStr + payStr + '</span>';
    }

    el.innerHTML = inner;
    container.appendChild(el);
  }

  /* ─── Should a non-BOGO rule be suppressed when BOGO is active? ──
     Suppress only when ALL of the non-BOGO rule's qualifying items are
     already covered by the BOGO (i.e. there's nothing extra to discount).
     If the cart has items outside the BOGO scope, keep the other rule visible. */
  function bogoCoversAllOf(bogoRule, otherRule, cartItems) {
    // Product IDs that the BOGO targets (null = covers the whole cart)
    var bogoCoveredIds;
    if (bogoRule.prodId) {
      bogoCoveredIds = [bogoRule.prodId];
    } else if (bogoRule.colIds.length > 0) {
      bogoCoveredIds = bogoRule.colIds.slice();
    } else {
      bogoCoveredIds = null; // BOGO is itself cart-wide → covers everything
    }

    if (bogoCoveredIds === null) return true; // BOGO cart-wide → suppress everything

    // Items the other rule applies to
    var otherItems;
    if (otherRule.prodId) {
      otherItems = cartItems.filter(function (i) { return String(i.product_id) === otherRule.prodId; });
      // Suppress if every qualifying item is also a BOGO item
      return otherItems.length > 0 &&
             otherItems.every(function (i) { return bogoCoveredIds.indexOf(String(i.product_id)) !== -1; });
    }
    if (otherRule.colIds.length > 0) {
      otherItems = cartItems.filter(function (i) { return otherRule.colIds.indexOf(String(i.product_id)) !== -1; });
      return otherItems.length > 0 &&
             otherItems.every(function (i) { return bogoCoveredIds.indexOf(String(i.product_id)) !== -1; });
    }
    // other rule is cart-wide: suppress only if there are NO items outside the BOGO
    var hasNonBogoItems = cartItems.some(function (i) {
      return bogoCoveredIds.indexOf(String(i.product_id)) === -1;
    });
    return !hasNonBogoItems;
  }

  /* ─── Main update ───────────────────────────────────────── */
  var _cartCache   = null;
  var _activeBogos = []; // eligible BOGO rules in current cart — used by calcSavings

  function updateDisplay(cart) {
    var container = document.getElementById('cs-discount-display');
    if (!container) return;

    var rules = readRules();
    container.innerHTML = '';
    if (!rules.length) return;

    var eligible  = [];
    var inProgress = [];

    // Identify eligible BOGO rules first so calcSavings can exclude their items
    // from cart-wide rules (e.g. flat20 should not count SOOP Advanced when BOGO is active)
    _activeBogos = rules.filter(function (rule) {
      return rule.type === 'bogo' && isValidNow(rule.startsAt, rule.endsAt) && evaluate(rule, cart).eligible;
    });

    rules.forEach(function (rule) {
      if (!rule.showInCart) return;
      if (!isValidNow(rule.startsAt, rule.endsAt)) return;
      var result = evaluate(rule, cart);
      if (result.eligible) {
        eligible.push({ rule: rule });
      } else if (result.progress) {
        inProgress.push({ rule: rule, progress: result.progress });
      }
    });

    if (!eligible.length && !inProgress.length) return;

    // BOGO priority: suppress non-BOGO banners only when they share items with an eligible BOGO.
    // Discounts targeting DIFFERENT products are kept — they show alongside BOGO.
    var bogoEligible = eligible.filter(function (item) { return item.rule.type === 'bogo'; });
    if (bogoEligible.length > 0) {
      eligible = eligible.filter(function (item) {
        if (item.rule.type === 'bogo') return true;
        return !bogoEligible.some(function (b) {
          return bogoCoversAllOf(b.rule, item.rule, cart.items || []);
        });
      });
    }

    var wrap = document.createElement('div');
    wrap.className = 'cs-disc-wrap';

    eligible.forEach(function (item)    { wrap.appendChild(buildEligible(item.rule, cart)); });
    inProgress.forEach(function (item)  { wrap.appendChild(buildProgress(item.rule, item.progress)); });

    container.appendChild(wrap);
    updateSavingsPreview(cart);
  }

  function fetchAndUpdate() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { _cartCache = cart; updateDisplay(cart); })
      .catch(function () {});
  }

  /* ─── Read embedded cart JSON (injected by cart-mini-footer.liquid) ── */
  function readEmbeddedCart() {
    var el = document.getElementById('cs-cart-json');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function updateFromEmbeddedOrCache() {
    var cart = readEmbeddedCart();
    if (cart) { _cartCache = cart; updateDisplay(cart); return true; }
    if (_cartCache) { updateDisplay(_cartCache); return true; }
    return false;
  }

  /* ─── Re-populate when XO re-renders cart footer ───────── */
  var _mutObs = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var nodes = mutations[i].addedNodes;
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.nodeType !== 1) continue;
        var hasDiscount = n.id === 'cs-discount-display' || (n.querySelector && n.querySelector('#cs-discount-display'));
        var hasPreview  = n.id === 'cs-savings-preview'  || (n.querySelector && n.querySelector('#cs-savings-preview'));
        if (hasDiscount || hasPreview) {
          // Try embedded JSON first (instant), fall back to fetch
          if (!updateFromEmbeddedOrCache()) fetchAndUpdate();
          return;
        }
      }
    }
  });
  _mutObs.observe(document.body, { childList: true, subtree: true });

  /* ─── Cart change listener ──────────────────────────────── */
  document.addEventListener('cart:updated', function () {
    setTimeout(fetchAndUpdate, 250); // let XO re-render first
  });

  /* ─── Init ──────────────────────────────────────────────── */
  function init() {
    // Try embedded cart data first (available after cart section renders)
    if (!updateFromEmbeddedOrCache()) fetchAndUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
